import React, { useCallback, useMemo } from 'react'
import NotificationBuilderMain from '../../../elements/notification-builder-main'
import { NoActiveChannelsBanner } from '../../../elements/no-active-channels-banner'
import NotificationVariantBuilder from '../../../elements/notification-variant-builder'
import NotificationAudienceBuilder from '../../../elements/notification-audience-builder'
import NotificationTestBuilder from '../../../elements/notification-test-builder'
import { AppService, NotificationService } from '../../../../../services'
import { INotificationBuilderState, TemplateDetails } from '../../../interfaces/notification-builder-state.interface'
import { DeliveryChannel } from '@pushly/models/lib/enums'
import { DomainDto } from '../../../../../dtos/domain'
import { NotificationDispatchActionPack } from '../../../types'
import { NotificationVariantModel } from '../../../../../models/notification/notification-variant.model'
import { useRefEffect } from '../../../../../hooks/use-ref-effect'
import {
    getCustomMacroFields,
    getDomainKeywords,
    getEnabledDeliveryChannels,
    getSecretsMacroFields,
} from '../../../../../_utils/domain'
import { getComputedCancelText, getComputedSubmitText, buildSubmittedNotificationPackages } from '../../../helpers'
import { NotificationAudienceModel } from '../../../../../models/notification/notification-audience.model'
import { NotificationTestConfigurationModel } from '../../../../../models/notification/notification-test-configuration.model'
import { Alert, Skeleton, notification } from 'antd'
import { PlusCircleFilled, LinkOutlined } from '@ant-design/icons'
import FlagList from '../../../../../structs/flags-list'
import { NotificationTemplateSelector } from '../../../../notification-template-selector/notification-template-selector'
import { DeliveryChannelSelector } from '@pushly/aqe/lib/components/delivery-channel-selector/delivery-channel-selector'
import {
    NotificationBuilderViewBuilderProps,
    NotificationBuilderViewMode,
    NotificationBuilderViewType,
    NotificationCatalogItemPickProps,
} from '../types'
import { NotificationBuilderSubmitType } from '../../../enums'
import { ApiVersion } from '../../../../../enums/api-version.enum'
import { registerUserPuuid } from '../../../elements/notification-builder-preview'
import { isIOSNonStandalone } from '../../../../../mobile-view/utils/ios-detection'
import { showIOSInstallPrompt } from '../../../../../mobile-view/components/ios-install-prompt'
import { TemplateDetailsBuilder } from '../../../../../features/notifications/named-notification-template/template-details-builder'
import NotificationCatalogItemOptions from '../../../elements/notification-catalog-item-options'
import OrgNotificationAudienceBuilder from '../../../elements/org-notification-audience-builder'
import { PlatformLevel } from '@pushly/aqe/lib/types/platform-level'
import { AccountDto } from '../../../../../dtos/account.dto'
import { getAccountEnabledDeliveryChannels } from '../../../../../_utils/account'
import { useNotificationBuilderViewSubmitCallback } from '../hooks/use-notification-builder-view-submit-callback'
import { useNotificationBuilderViewCancelCallback } from '../hooks/use-notification-builder-view-cancel-callback'
import { FEAT_NOTIF_FIELD_SUGGESTIONS, FEAT_PUSHLY_AI } from '../../../../../constants'
import { Journey } from '../../../../../features/journeys/types/journey-api-response'
import { extractJourneyTriggerType } from '../../../../../utils/journeys'
import { useUserPreferences } from '@pushly/aqe/lib/providers/user-preferences-provider/core'
import { useService } from '@pushly/aqe/lib/hooks'
import { AppState } from '../../../../../stores/app'

const PREFS_KEY_AB_UX_CHANGE_BANNER_DISMISSED = 'notifications:ab-ux-changed-banner-dismissed'

type TemplateChangeSet = {
    id?: number | undefined
    domain: DomainDto
    usingTemplate?: boolean | undefined
}

type BuilderActionHandlers = {
    onSubmit: (isDraft?: boolean) => void | Promise<void>
    onCancel: () => void | Promise<void>
}

type NotificationBuilderViewMainProps = {
    level: PlatformLevel
    formType: NotificationBuilderViewType
    formMode: NotificationBuilderViewMode
    loading: boolean
    domain: DomainDto
    org?: AccountDto
    builder: INotificationBuilderState
    builderProps?: NotificationBuilderViewBuilderProps
    isMobile: boolean
    isInDrawer: boolean
    requestedNotifId?: number
    requestedTemplateId?: number
    requestedTestId?: number
    hideDeliveryChannels: boolean
    hideAudienceSelection: boolean
    hideTestConfig: boolean
    hideTemplateSelector: boolean
    hideVariantManager: boolean
    hideScheduling: boolean
    hideSubmit: boolean
    hidePageAnalysisToggle: boolean
    hidePushlyAiToggle: boolean
    showSendToDeviceInFooter?: boolean
    showTemplateDetails: boolean
    submitText?: string
    cancelText?: string
    journey?: Journey
    journeyNotificationType?: string
    catalogItemPick: NotificationCatalogItemPickProps | false
    readonlyVariantManager: boolean
    nestedDeliveryChannels: boolean
    dispatchChanges: (action: NotificationDispatchActionPack) => void
    getParentRoute?: (isDraft: boolean) => string
    onTemplateChange: (change: TemplateChangeSet) => void
    onTemplateDetailsChange: (change: TemplateDetails) => void
    onVariantChange: (variant: NotificationVariantModel, id: number) => void
    onChannelChange: (change: DeliveryChannel[]) => void
    onAudienceChange: (value: NotificationAudienceModel) => any
    onTestChange: (value: NotificationTestConfigurationModel | null) => any
    onInitialVariantInit?: (variant: NotificationVariantModel) => void
    onSubmit?: (isDraft: boolean, builderState: INotificationBuilderState) => void | Promise<void>
    onSubmitCompleted?: (success: boolean, isDraft: boolean) => void | Promise<void>
    onCancel?: () => void | Promise<void>
    validateSubmit: (
        builder: INotificationBuilderState,
        domain: DomainDto,
        flags: FlagList,
        isPreview?: boolean,
        isDraft?: boolean,
        appService?: AppService,
    ) => Promise<boolean>
}

export const NotificationBuilderViewMain = (props: NotificationBuilderViewMainProps) => {
    const {
        level,
        formType,
        formMode,
        loading,
        domain: defaultDomain,
        org,
        builder,
        builderProps = {},
        isMobile,
        isInDrawer,
        requestedNotifId,
        requestedTemplateId,
        requestedTestId,
        catalogItemPick,
        journey,
        journeyNotificationType,
        hideDeliveryChannels,
        hideAudienceSelection,
        hideTestConfig,
        hideTemplateSelector,
        hideVariantManager,
        hideScheduling,
        hidePageAnalysisToggle,
        hidePushlyAiToggle,
        hideSubmit,
        showSendToDeviceInFooter,
        showTemplateDetails,
        readonlyVariantManager,
        submitText: submitTextOverride,
        cancelText: cancelTextOverride,
        nestedDeliveryChannels,
        dispatchChanges,
        getParentRoute,
        onTemplateChange,
        onTemplateDetailsChange,
        onVariantChange,
        onChannelChange,
        onAudienceChange,
        onTestChange,
        onInitialVariantInit,
        onSubmit,
        onSubmitCompleted,
        onCancel,
        validateSubmit,
    } = props

    const activeVariant = builder.variants[builder.selectedVariantIdx]

    const selectedDomainIds = useMemo(() => {
        return level === 'domain'
            ? [defaultDomain.id]
            : activeVariant.getAudience().getDomainIds() ?? builder.availableDomains.map((d) => d.id)
    }, [level, defaultDomain, activeVariant.getAudience().getDomainIds(), builder.availableDomains])

    // primary domain to use for the builder
    const domain = useMemo(() => {
        if (level === 'domain') {
            return defaultDomain
        }

        return builder.availableDomains.find((d) => d.id === selectedDomainIds[0]) ?? defaultDomain
    }, [level, selectedDomainIds, builder.availableDomains])

    const primaryEntityHasPageAnalysisFlag = useMemo(() => {
        const flags = level === 'domain' ? domain.flags : org?.flags ?? []
        return flags.includes(FEAT_NOTIF_FIELD_SUGGESTIONS)
    }, [level, domain, org])

    const primaryEntityHasPushlyAiFlag = useMemo(() => {
        const flags = level === 'domain' ? domain.flags : org?.flags ?? []
        return flags.includes(FEAT_PUSHLY_AI)
    }, [level, domain, org])

    // Specific domain used for the Page Analysis field configurations
    const pageAnalysisDomain = useMemo(() => {
        if (level === 'domain') {
            return defaultDomain
        }

        return builder.availableDomains
            .filter((d) => selectedDomainIds.includes(d.id))
            .find((d) => {
                const cs = d.notificationFieldSettings?.content_suggestions
                return (
                    d.flags.includes(FEAT_NOTIF_FIELD_SUGGESTIONS) &&
                    (d.notificationFieldSettings == null || cs == null || cs.length > 0)
                )
            })
    }, [level, selectedDomainIds, builder.availableDomains])

    const hidePageAnalysisControl = hidePageAnalysisToggle || !primaryEntityHasPageAnalysisFlag || !pageAnalysisDomain

    const hidePushlyAiControl = hidePushlyAiToggle || !primaryEntityHasPushlyAiFlag

    const handleCancel = useNotificationBuilderViewCancelCallback({
        domainId: domain.id,
        consumerCancelHandler: onCancel,
    })

    const handleSubmit = useNotificationBuilderViewSubmitCallback({
        level,
        formType,
        formMode,
        domain,
        account: org,
        builder,
        requestedNotifId,
        requestedTemplateId,
        requestedTestId,
        validateSubmit,
        consumerOnSubmitCompletedHandler: onSubmitCompleted,
        consumerSubmitHandler: onSubmit,
        resolveParentRoute: getParentRoute,
    })

    const sourceTemplateId = builder.variants.find((v) => v.getDisplayMeta().sourceTemplateId)?.getDisplayMeta()
        .sourceTemplateId

    const orgEnabledDeliveryChannels = org ? getAccountEnabledDeliveryChannels(org) : []
    const domainActiveDeliveryChannels = getEnabledDeliveryChannels(domain, true)
    const workingDeliveryChannels = level === 'org' ? orgEnabledDeliveryChannels : domainActiveDeliveryChannels
    const isMultiChannelContext = workingDeliveryChannels.length > 1

    const isCatalogItemNotification = catalogItemPick !== false

    const hideTemplateSelection = hideTemplateSelector || !builder.availableTemplates.length
    const hideStandaloneDeliveryChannels = hideDeliveryChannels || !isMultiChannelContext || nestedDeliveryChannels
    const hideNestedDeliveryChannels = hideDeliveryChannels || !isMultiChannelContext || !nestedDeliveryChannels
    const hideTestConfiguration = hideTestConfig || !(builder.test || builder.editNotificationType === 'test')

    const [keywordOptions] = useRefEffect(() => {
        if (builderProps.keywordOptions?.current) {
            return builderProps.keywordOptions.current
        }

        if (org) {
            return org.keywords?.map((kw) => ({
                value: kw,
                label: kw,
            }))
        }

        return getDomainKeywords(domain.id)
    }, [domain.id])

    const [customMacroOptions] = useRefEffect(() => {
        if (builderProps.customMacroOptions?.current) {
            return builderProps.customMacroOptions?.current
        }

        if (org) {
            // ORG level does not currently have default custom macros
            return undefined
        }

        return getCustomMacroFields(domain.id)
    }, [domain.id])

    const [secretsMacroOptions] = useRefEffect(() => {
        return getSecretsMacroFields(domain.id)
    }, [domain.id])

    const actionHandlers: BuilderActionHandlers = useMemo(
        () => ({
            onSubmit: handleSubmit,
            onCancel: handleCancel,
        }),
        [handleSubmit, handleCancel],
    )

    const submitText = getComputedSubmitText({
        formMode,
        formType,
        variants: builder.variants,
        override: submitTextOverride,
    })

    const cancelText = getComputedCancelText(cancelTextOverride)

    const appState = useService(AppState)
    const notifService = useService(NotificationService)

    const handlePreviewRequest = useCallback(
        async (requestType: NotificationBuilderSubmitType) => {
            const user = appState.currentUser
            if (!user) return

            const valid = await validateSubmit(builder, domain, appState.flags, true)
            if (!valid) return

            // iOS requires standalone (PWA) mode for web push.
            // If the user is on iOS Safari (not installed to home screen), show instructions.
            if (isIOSNonStandalone()) {
                showIOSInstallPrompt()
                return
            }

            try {
                await registerUserPuuid(user, domain)

                const campaignInfo = {
                    campaign_id: journey?.id ?? null,
                    campaign_notification_type: journeyNotificationType,
                }

                const { notifications } = buildSubmittedNotificationPackages(
                    domain,
                    builder,
                    appState.flags,
                    requestType,
                    campaignInfo,
                )
                const previewBuild = notifications[builder.selectedVariantIdx]

                await notifService.sendNotification(domain.id, previewBuild, {
                    cancellationKey: 'notif-send-preview-mobile',
                    version: ApiVersion.V4,
                })
            } catch {
                notification.error({
                    key: 'preview-send-failed',
                    message: undefined,
                    description: 'Failed to send preview. Please try again.',
                    duration: 5,
                })
            }
        },
        [appState, builder, domain, validateSubmit, journey, journeyNotificationType, notifService],
    )

    const extraSubmitActions = useMemo(() => {
        if (!showSendToDeviceInFooter) return undefined
        return [
            {
                text: 'Send Personal Preview',
                onClick: () => handlePreviewRequest(NotificationBuilderSubmitType.PERSONAL_PREVIEW),
            },
            {
                text: 'Send Team Preview',
                onClick: () => handlePreviewRequest(NotificationBuilderSubmitType.TEAM_PREVIEW),
            },
        ]
    }, [showSendToDeviceInFooter, handlePreviewRequest])

    const contentAndDeliveryActions: Partial<BuilderActionHandlers> = hideTestConfiguration ? actionHandlers : {}
    const testConfigActions: Partial<BuilderActionHandlers> = !hideTestConfiguration ? actionHandlers : {}

    const isLoading = loading || builder.loading

    // A/B testing info banner — domain send/edit only (not templates, not campaigns)
    const showAbTestBanner = level === 'domain' && formType === 'send' && !journey
    const userPreferences = useUserPreferences(appState.currentUser?.id, defaultDomain.id)
    const abTestBannerDismissed = userPreferences.get(['extras', PREFS_KEY_AB_UX_CHANGE_BANNER_DISMISSED], false)
    const abTestBannerVisible = showAbTestBanner && !abTestBannerDismissed

    // Same surfaces as the A/B banner. Reads the active-only channel list (web by flag, native by
    // active credential) that the builder seeds its targeted channels from.
    const showNoActiveChannelsBanner = showAbTestBanner && domainActiveDeliveryChannels.length === 0

    const handleAbTestBannerClose = useCallback(() => {
        userPreferences.setUnscoped(['extras', PREFS_KEY_AB_UX_CHANGE_BANNER_DISMISSED], true)
    }, [userPreferences])

    if (isLoading) {
        return <Skeleton active={true} loading={true} />
    }

    return (
        <NotificationBuilderMain>
            {showNoActiveChannelsBanner && <NoActiveChannelsBanner />}

            {!hideTemplateSelection && (
                <NotificationTemplateSelector
                    builder={builder}
                    domain={domain}
                    onChange={onTemplateChange}
                    value={builder.selectedTemplate}
                    isInDrawer={isInDrawer}
                />
            )}

            {!hideStandaloneDeliveryChannels && (
                <DeliveryChannelSelector
                    type="multiple"
                    loading={builder.loading}
                    value={builder.channels}
                    onChange={onChannelChange}
                    visibleChannels={workingDeliveryChannels}
                    compact={isMobile}
                    wellProps={{
                        variant: isInDrawer ? 'ghost' : 'default',
                    }}
                />
            )}

            {showTemplateDetails && (
                <TemplateDetailsBuilder
                    loading={builder.loading}
                    value={builder.details}
                    onChange={onTemplateDetailsChange}
                    isInDrawer={isInDrawer}
                />
            )}

            {abTestBannerVisible && (
                <Alert
                    type="info"
                    closable
                    onClose={handleAbTestBannerClose}
                    message="A/B testing has moved"
                    description={
                        <p style={{ margin: 0 }}>
                            A/B testing is now automatically enabled when you add more than one notification variant.
                            Use the <PlusCircleFilled style={{ color: 'var(--color-secondary-90)' }} /> button next to
                            the variant tabs in the Content section to create additional variants. Test naming appears
                            at the end of the form when multiple variants are added.&nbsp;
                            <a
                                href="https://documentation.pushly.com/platform/notifications/a-b-testing"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#1890ff' }}
                            >
                                Learn more <LinkOutlined />
                            </a>
                        </p>
                    }
                    style={{ boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.15)' }}
                />
            )}

            {hideAudienceSelection ? null : level === 'org' && org ? (
                <OrgNotificationAudienceBuilder
                    loading={builder.loading}
                    disabled={formMode !== 'create'}
                    segmentSelectDisabled={activeVariant.getDisplayMeta().notEditable}
                    account={org}
                    setBuilder={dispatchChanges}
                    value={activeVariant.getAudience()}
                    onChange={onAudienceChange}
                    isInDrawer={isInDrawer}
                />
            ) : (
                <NotificationAudienceBuilder
                    loading={builder.loading}
                    domain={domain}
                    setBuilder={dispatchChanges}
                    value={activeVariant.getAudience()}
                    onChange={onAudienceChange}
                    channels={builder.channels}
                    isInDrawer={isInDrawer}
                />
            )}

            {isCatalogItemNotification && (
                <NotificationCatalogItemOptions
                    loading={builder.loading}
                    builder={builder}
                    dispatchChanges={dispatchChanges}
                    options={catalogItemPick?.options ?? []}
                    defaultSelectedStrategy={catalogItemPick.defaultSelectedOption}
                    itemType={catalogItemPick.itemType}
                    // Extract the trigger type for context-aware labels
                    triggerType={extractJourneyTriggerType(journey)}
                />
            )}

            <NotificationVariantBuilder
                key={activeVariant.getId()}
                isTemplate={formType === 'named-template'}
                onChannelChange={onChannelChange}
                id={builder.selectedVariantIdx}
                domain={domain}
                pageAnalysisDomain={pageAnalysisDomain}
                builder={builder}
                dispatchChanges={dispatchChanges}
                onInitialVariantInit={onInitialVariantInit}
                variant={activeVariant}
                hideChannelSelector={hideNestedDeliveryChannels}
                hideVariantManager={hideVariantManager}
                hideDelivery={hideScheduling}
                hidePageAnalysisToggle={hidePageAnalysisControl}
                hidePushlyAiToggle={hidePushlyAiControl}
                variantManagerIsReadonly={readonlyVariantManager}
                onChange={onVariantChange}
                keywordOptions={keywordOptions.current}
                customMacroOptions={customMacroOptions.current}
                secretsMacroOptions={secretsMacroOptions.current}
                hideSubmit={hideSubmit || !hideTestConfiguration}
                submitText={submitText}
                cancelText={cancelText}
                extraSubmitActions={extraSubmitActions}
                isInDrawer={isInDrawer}
                {...contentAndDeliveryActions}
            />

            {!hideTestConfiguration && (
                <NotificationTestBuilder
                    builder={builder}
                    loading={builder.loading}
                    value={builder.test}
                    onChange={onTestChange}
                    hideSubmit={hideSubmit}
                    submitText={submitText}
                    cancelText={cancelText}
                    isInDrawer={isInDrawer}
                    {...testConfigActions}
                />
            )}
        </NotificationBuilderMain>
    )
}
